// =====================================================================================
// INVRT Radar — web console — the contact drawer
//
// One record from GET /contacts/:id (docs/API-CONTRACTS-C1.md §5), shown in full: the
// person, the company, the visit (pages and campaign, visitors only), the credits exactly
// as the database holds them, and the actions. Shared by the Contacts page and a site's
// Visitors tab, so a contact reads the same wherever it is opened from.
//
// Built on the native <dialog> element, as components/ui/dialog.tsx is: focus trapping,
// Escape and the top layer come from the platform. The send and push dialogs are rendered
// as SIBLINGS of the drawer rather than inside it, so each is its own top-layer entry and
// Escape closes only the one in front.
//
// Sending is an admin action on the server (routes/contacts.ts requireDeliveryWrite), so
// the send and push buttons are shown only to an organisation admin. Reading is open to
// every member, and so is the bridge preview (any member may spend, exactly as on the Find
// people page).
//
// C4: the read (useContactDetail) and the dossier body (ContactDossierBody, with the
// badge row, ContactBadges) are EXPORTED from here, and the drawer stays the in-list
// quick view; /contacts/:id (ContactDossierPage) renders the same body as a
// deep-linkable page, so the two surfaces can never disagree about what the wire said.
//
// C7 (mode-aware): the shared body also carries the site's mode fork. The contact's
// site's mode (the site row's provider_plan_type, employee to b2b and consumer to b2c,
// exactly as routes/sites.ts's tenantPlanType maps it) is read from this organisation's
// own sites list both surfaces already hold, so the drawer and the page reorder together:
// a b2c site's dossier leads with the COMPANY section, a b2b site keeps today's
// person-first order. Emphasis only - the badge row still states the record's own
// identified level in both modes, and a mode that is not set (or a sites list still
// loading) falls open to today's order.
// =====================================================================================

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Copy, ExternalLink, Mail, Send, X } from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { PulseRequestPanel } from "@/components/pulse/PulseRequestPanel";
import { useQuery } from "@tanstack/react-query";
import { useApiClient, useSites } from "@/hooks/useApi";
import { useToast } from "@/components/ui/toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ContactCard } from "@/components/ContactCard";
import { ErrorState, LoadingState } from "@/components/States";
import { formatCreditsWithUnit, formatDateTime } from "@/lib/format";
import { siteModeOf } from "@/lib/siteMode";
import type { Contact } from "@/lib/types.c1";
import { FindPeoplePanel } from "./FindPeoplePanel";
import { PushToTargetDialog } from "./PushToTargetDialog";
import { SendContactsDialog } from "./SendContactsDialog";
import {
  formatLocation,
  sourceLabel,
  useIsOrgAdmin,
} from "./useContacts";

export interface ContactDrawerProps {
  /** `vis_<uuid>` or `rev_<uuid>`. */
  contactId: string;
  onClose: () => void;
  /** The list row, when the caller has one: shown whilst the full record loads. */
  initial?: Contact;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink">{children}</dd>
    </div>
  );
}

function Section({
  title,
  level,
  children,
}: {
  title: string;
  level: 2 | 3;
  children: ReactNode;
}) {
  const Heading = level === 2 ? "h2" : "h3";
  return (
    <section className="flex flex-col gap-3">
      <Heading className="text-sm font-semibold text-ink">{title}</Heading>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">{children}</dl>
    </section>
  );
}

function OutboundLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
    >
      {children}
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
      {/* Story O9: one seam for every drawer/page new-tab link; the warning rides in the
          accessible name so a screen reader hears it before activating. */}
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  );
}

// O8: the mode mapping is shared (lib/siteMode); the drawer imports it like the
// visitors table does, so the two surfaces cannot drift.
const DASH = "—";

// ---- C4 extraction: the one read and the one body behind both dossier surfaces ----------

/** The subject a PULSE enrichment may be quoted for, once this contact's read has settled. */
export interface PulseSubject {
  type: "contact" | "company";
  key: string;
}

/**
 * The one read behind both dossier surfaces: GET /contacts/:id, re-read whenever the
 * session or organisation changes, together with the settled-read gate that decides
 * whether a PULSE quote may be asked for this exact record. `initial` (the list row, when
 * the caller has one) shows while the full record loads and is never shown as final.
 *
 * Co-located with the dossier components it feeds, per eslint.config.js's stated pattern
 * (useToast with ToastProvider, useSession with SessionProvider); that file's
 * allowExportNames list is the permanent home for this name.
 */
export function useContactDetail(contactId: string, initial?: Contact) {
  const {orgId,session} = useSession();
  const pulseApi=useApiClient();
  const contact=useQuery({queryKey:["contacts",orgId,"detail",contactId,session?.user.id,session?.user.last_sign_in_at],queryFn:({signal})=>pulseApi.get<Contact>(`/contacts/${contactId}`,{signal}),enabled:Boolean(orgId&&session&&contactId!==""),...(initial?{placeholderData:initial}:{}),retry:false,staleTime:0,refetchOnMount:"always"});
  // A quote may only name a subject the server has just answered for THIS contact's own
  // read: not a placeholder list row, not an answer carried over from another id.
  const scope = `${orgId}:${session?.user.id}:${contactId}`;
  const freshSince = useRef({scope, at:Date.now()});
  if(freshSince.current.scope!==scope) freshSince.current={scope,at:Date.now()};

  const record = contact.data ?? null;
  let pulseSubject: PulseSubject | null = null;
  if (
    contact.isSuccess &&
    !contact.isPlaceholderData &&
    !contact.isFetching &&
    contact.dataUpdatedAt >= freshSince.current.at &&
    record !== null &&
    record.id === contactId
  ) {
    if (typeof record.person.email === "string" && record.person.email) {
      pulseSubject = { type: "contact", key: record.person.email };
    } else if (typeof record.company.domain === "string" && record.company.domain) {
      pulseSubject = { type: "company", key: record.company.domain };
    }
  }
  return { contact, record, pulseSubject };
}

/** The identity badges under the name: source, level, known customer and the C3 visits. */
export function ContactBadges({ record }: { record: Contact }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge tone={record.source === "identified" ? "info" : "success"}>
        {sourceLabel(record.source)}
      </Badge>
      {record.identified_level ? (
        <Badge tone="neutral">
          {record.identified_level === "person" ? "Person level" : "Company level"}
        </Badge>
      ) : null}
      {record.is_known_customer ? <Badge tone="neutral">Already a customer</Badge> : null}
      {/* C3: the engagement facts the server counted from this organisation's
          own visit records. Facts only: the count as held, no score. */}
      {record.engagement ? (
        <Badge tone="info">
          {record.engagement.visit_count}{" "}
          {record.engagement.visit_count === 1 ? "visit" : "visits"}
        </Badge>
      ) : null}
    </div>
  );
}

export interface ContactDossierBodyProps {
  /** The record on screen; the caller renders the loading and error states around it. */
  record: Contact;
  /** The contact's site named by its domain, or null when no site or no match. */
  siteDomain: string | null;
  /** Organisation admins see the send and push actions; the server enforces the same rule. */
  isAdmin: boolean;
  /** True while the record on screen is the list-row placeholder (pages and campaign pending). */
  isPlaceholder: boolean;
  pulseLoading: boolean;
  pulseSubject: PulseSubject | null;
  onRequestSend: () => void;
  onRequestPush: () => void;
  /**
   * The level of the section headings. The drawer's dialog title is an h2, so its sections
   * are h3 (the default); the dossier page's title is an h1, so it passes 2 and the
   * headings do not skip a level.
   */
  headingLevel?: 2 | 3;
  /**
   * Story O2 (Mark ruling 7): show the Visit section's flat "Pages viewed" list. True
   * (the default) keeps the drawer's full visit read-out; the dossier page passes false
   * because the person journey timeline under the body is the page's one rendering of
   * the page views. The Campaign attribution is NOT governed by this prop: it lives in
   * the Record section, so it renders on both surfaces either way.
   */
  showVisitSection?: boolean;
}

/**
 * The dossier body: actions, PULSE panel, person, company, record and, for an identified
 * visitor, the visit and the find-people bridge. Rendered by the drawer and, since C4, by
 * the ContactDossierPage at /contacts/:id - one body, two surfaces, no divergence.
 */
export function ContactDossierBody({
  record,
  siteDomain,
  isAdmin,
  isPlaceholder,
  pulseLoading,
  pulseSubject,
  onRequestSend,
  onRequestPush,
  headingLevel = 3,
  showVisitSection = true,
}: ContactDossierBodyProps) {
  const toast = useToast();
  // C7: the contact's site's mode, read from this organisation's own sites list - the read
  // both surfaces (the drawer's quick view and the dossier page) already hold - so the two
  // surfaces can never disagree about the emphasis. A site that is not on the list (a
  // revealed person has no site) and a list still loading keep today's order.
  const sites = useSites();
  const siteMode = siteModeOf(
    record.site_id
      ? sites.data?.sites.find((site) => site.id === record.site_id)?.provider_plan_type
      : undefined,
  );

  async function copyEmail(email: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(email);
      toast.push({ tone: "success", title: "Email address copied" });
    } catch {
      toast.push({
        tone: "warning",
        title: "Could not copy automatically",
        body: "Select the address and copy it by hand.",
      });
    }
  }

  // The two identity sections, lifted so the site's mode can order them (C7): a b2c
  // site's dossier leads with the COMPANY, a b2b site keeps today's person-first order.
  const personSection = (
    <Section title="Person" level={headingLevel}>
      <Field label="Title">{record.person.title ?? DASH}</Field>
      <Field label="Email">
        {record.person.email ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="break-all font-mono text-xs">{record.person.email}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              aria-label="Copy email address"
              onClick={() => void copyEmail(record.person.email ?? "")}
            >
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </span>
        ) : (
          DASH
        )}
      </Field>
      <Field label="Phone">
        {record.person.phone ? (
          <span className="font-mono text-xs">{record.person.phone}</span>
        ) : (
          DASH
        )}
      </Field>
      <Field label="LinkedIn">
        {record.person.linkedin_url ? (
          <OutboundLink href={record.person.linkedin_url}>Profile</OutboundLink>
        ) : (
          DASH
        )}
      </Field>
      <Field label="Location">{formatLocation(record.person.location) ?? DASH}</Field>
    </Section>
  );

  const companySection = (
    <Section title="Company" level={headingLevel}>
      <Field label="Name">{record.company.name ?? DASH}</Field>
      <Field label="Domain">{record.company.domain ?? DASH}</Field>
      <Field label="Industry">{record.company.industry ?? DASH}</Field>
      <Field label="Size">{record.company.employee_range ?? DASH}</Field>
      <Field label="LinkedIn">
        {record.company.linkedin_url ? (
          <OutboundLink href={record.company.linkedin_url}>Company page</OutboundLink>
        ) : (
          DASH
        )}
      </Field>
      <Field label="Location">{formatLocation(record.company.location) ?? DASH}</Field>
    </Section>
  );

  return (
    <div className="flex flex-col gap-6">
      {isAdmin ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="min-h-11" onClick={onRequestSend}>
            <Mail className="h-3.5 w-3.5" aria-hidden="true" />
            Send by email
          </Button>
          <Button variant="secondary" size="sm" className="min-h-11" onClick={onRequestPush}>
            <Send className="h-3.5 w-3.5" aria-hidden="true" />
            Push to a target
          </Button>
        </div>
      ) : null}

      <PulseRequestPanel loading={pulseLoading} subject={pulseSubject} headingLevel={headingLevel} />

      {/* C7: a b2c site's dossier leads with the COMPANY; a b2b site keeps today's
          person-first order. Emphasis only: the badge row above still states the record's
          own identified level in both modes. */}
      {siteMode === "b2c" ? (
        <>
          {companySection}
          {personSection}
        </>
      ) : (
        <>
          {personSection}
          {companySection}
        </>
      )}

      <Section title="Record" level={headingLevel}>
        <Field label={record.source === "identified" ? "Seen" : "Revealed at"}>
          {formatDateTime(record.occurred_at)}
        </Field>
        <Field label="Site">{siteDomain ?? DASH}</Field>
        {/* C3: the lifetime facts behind this contact, exactly as the server
            counted them from the organisation's own visit records. */}
        {record.engagement ? (
          <>
            <Field label="First seen">
              {formatDateTime(record.engagement.first_seen_at)}
            </Field>
            <Field label="Last activity">
              {formatDateTime(record.engagement.last_activity_at)}
            </Field>
          </>
        ) : null}
        {/* Exactly as the database holds it; formatted, never computed. Null for a
            revealed person, whose charge sits once on the quote. */}
        <Field label="Credits">{formatCreditsWithUnit(record.credits)}</Field>
        <Field label="Reference">
          <span className="break-all font-mono text-xs text-ink-muted">{record.id}</span>
        </Field>
        {/* Story O2: the campaign attribution moved here from the Visit section, so it
            survives on both surfaces now the page hides the flat page-view list; the
            drawer reads it exactly as before, now beside the record facts. Visitors
            only: a revealed person has no campaign to show. */}
        {record.source === "identified" ? (
          <div className="flex flex-col gap-0.5 sm:col-span-2">
            <dt className="text-xs uppercase tracking-wide text-ink-muted">Campaign</dt>
            <dd className="text-sm text-ink">
              {isPlaceholder ? (
                <span className="text-ink-muted">Loading…</span>
              ) : record.campaign &&
                (record.campaign.campaign || record.campaign.source || record.campaign.medium) ? (
                <span className="flex flex-col">
                  <span>{record.campaign.campaign ?? DASH}</span>
                  <span className="text-xs text-ink-muted">
                    {[record.campaign.source, record.campaign.medium]
                      .filter((part): part is string => Boolean(part))
                      .join(" / ") || DASH}
                  </span>
                </span>
              ) : (
                <span className="text-ink-muted">No campaign was attributed.</span>
              )}
            </dd>
          </div>
        ) : null}
      </Section>

      {/* Story O2: the flat "Pages viewed" list is the drawer's visit read-out; the dossier
          page (showVisitSection false) leaves it off because the person journey timeline
          below the body is the page's one rendering of the page views. */}
      {record.source === "identified" && showVisitSection ? (
        <Section title="Visit" level={headingLevel}>
          <div className="flex flex-col gap-0.5 sm:col-span-2">
            <dt className="text-xs uppercase tracking-wide text-ink-muted">Pages viewed</dt>
            <dd className="text-sm text-ink">
              {isPlaceholder ? (
                <span className="text-ink-muted">Loading…</span>
              ) : record.pages && record.pages.length > 0 ? (
                <ul className="flex flex-col gap-1.5">
                  {record.pages.map((page, index) => (
                    <li key={`${page.url ?? ""}-${index}`} className="flex flex-col">
                      {page.url ? (
                        <OutboundLink href={page.url}>{page.title ?? page.url}</OutboundLink>
                      ) : (
                        <span>{page.title ?? DASH}</span>
                      )}
                      <span className="text-xs text-ink-muted">
                        {page.url && page.title ? `${page.url} · ` : ""}
                        {formatDateTime(page.viewed_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-ink-muted">No page views were recorded.</span>
              )}
            </dd>
          </div>
        </Section>
      ) : null}

      {record.source === "identified" && record.company.domain ? (
        <FindPeoplePanel domain={record.company.domain} headingLevel={headingLevel} />
      ) : null}
    </div>
  );
}

export function ContactDrawer({ contactId, onClose, initial }: ContactDrawerProps) {
  const { contact, record, pulseSubject } = useContactDetail(contactId, initial);
  const sites = useSites();
  const isAdmin = useIsOrgAdmin();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [sending, setSending] = useState(false);
  const [pushing, setPushing] = useState(false);

  // Open as a modal on mount; jsdom (and older Safari) has no showModal, so the `open`
  // attribute is the fallback, exactly as components/ui/dialog.tsx does.
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const invoker = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!element.open) {
      if (typeof element.showModal === "function") element.showModal();
      else element.setAttribute("open", "");
    }
    return () => {
      if (element.open) {
        if (typeof element.close === "function") element.close();
        else element.removeAttribute("open");
      }
      queueMicrotask(() => { if (invoker?.isConnected) invoker.focus(); });
    };
  }, []);

  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      event.preventDefault();
      onClose();
    },
    [onClose],
  );

  const siteDomain =
    record?.site_id
      ? (sites.data?.sites.find((site) => site.id === record.site_id)?.domain ?? record.site_id)
      : null;

  return (
    <>
      <dialog
        ref={ref}
        aria-labelledby={titleId}
        onCancel={handleCancel}
        onClick={(event) => {
          // A click on the backdrop lands on the dialog element itself; anything inside
          // lands on a child. Only the former closes.
          if (event.target === ref.current) onClose();
        }}
        className="fixed inset-y-0 left-auto right-0 m-0 h-full max-h-none w-[min(38rem,100vw)] max-w-none border-l border-line bg-surface p-0 text-ink shadow-raised backdrop:bg-ink/40"
      >
        <div className="flex h-full flex-col [&_button]:min-h-11 [&_button]:min-w-11">
          <div className="flex items-start justify-between gap-4 border-b border-line p-5">
            <div className="flex min-w-0 flex-col gap-2">
              {/* C5: the identity is the shared ContactCard's row form - the deterministic
                  mark (aria-hidden) beside the name, which stays in text. */}
              <h2 id={titleId} className="truncate text-lg font-semibold">
                {record ? <ContactCard variant="row" contact={record} /> : "Contact"}
              </h2>
              {record ? (
                <>
                  <ContactBadges record={record} />
                  {/* C4: the same dossier as a deep-linkable page; the drawer stays the
                      in-list quick view and hands off to the page here. */}
                  <Link
                    to={`/contacts/${contactId}`}
                    className="inline-flex items-center gap-1 text-xs text-primary underline-offset-2 hover:underline"
                  >
                    Open as a page
                  </Link>
                </>
              ) : null}
            </div>
            <Button variant="ghost" size="icon" className="h-11 w-11 shrink-0" onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            {contact.isLoading && !record ? <LoadingState label="Loading this contact…" /> : null}
            {contact.isError ? (
              <ErrorState error={contact.error} onRetry={() => void contact.refetch()} />
            ) : null}

            {record ? (
              <ContactDossierBody
                record={record}
                siteDomain={siteDomain}
                isAdmin={isAdmin}
                isPlaceholder={contact.isPlaceholderData}
                pulseLoading={contact.isFetching}
                pulseSubject={pulseSubject}
                onRequestSend={() => setSending(true)}
                onRequestPush={() => setPushing(true)}
              />
            ) : null}
          </div>
        </div>
      </dialog>

      <SendContactsDialog open={sending} contactIds={[contactId]} onClose={() => setSending(false)} />
      <PushToTargetDialog open={pushing} contactIds={[contactId]} onClose={() => setPushing(false)} />
    </>
  );
}
